import { Address } from '@/application/shared/domain/Address';
import { UUID } from '@hfc/common/src/types/UUID';
import { AppTypeDentalOrVision, DentalAndVisionApplication } from '@/application/shared/domain/DentalAndVisionApplication';
import { AddressValidationResponse } from '@/application/shared/domain/AddressValidationResponse';
import { ChallengeQuestion } from '@/application/shared/domain/ChallengeQuestion';
import { EnrollmentRepository } from '@/application/shared/domain/EnrollmentRepository';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import axios, { AxiosError, AxiosResponse } from 'axios';
import enrollmentAxios from '@/shared/infrastructure/Axios';
import recaptchaAxios from '@/shared/infrastructure/RecaptchaAxios';
import { CoverageResponse, ServicesApiResponse, SfApiResponse, RawServicesError, RawSfError } from '@hfc/ui-core';
import { ChallengeType, ChallengeQuestionSettings, ChallengeQuestionFields } from '@/application/shared/domain/ChallengeQuestionSettings';
import { ChallengeQuestionResponse } from '@/application/shared/domain/ChallengeQuestionResponse';
import { EffectiveDatesResponse } from '@/application/shared/domain/EffectiveDatesResponse';
import { FeedbackInfo } from '@/application/shared/domain/FeedbackInfo';
import { OtpVerificationCodeRequest } from '@/shared/domain/OtpVerificationCodeRequest';
import { OtpChannelType } from '@/shared/domain/OtpChannelType';
import { CCAccountDataResponse } from '@/shared/domain/CCAccountDataResponse';

export class EnrollmentApi implements EnrollmentRepository {

  public async validateAddress (address: Address): Promise < ServicesApiResponse<AddressValidationResponse> > {
    try {
      const axiosResponse: AxiosResponse = await axios.post(`${import.meta.env.VITE_VERIFICATION_API_URL}/v1/address/verify`, address);

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawServicesError>
      };
    }
  }

  public async checkCoverage (applicationType: APPLICATION_TYPES, zip_code: number): Promise<SfApiResponse<CoverageResponse>> {
    try {
      const axiosResponse = await axios.get(`${import.meta.env.VITE_APP_LEGACY_API_URL}/coverage/${applicationType}/${zip_code}`);

      return {
        success: true,
        data: axiosResponse.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async submitDentalAndVisionApplication (applicationType: AppTypeDentalOrVision, application: DentalAndVisionApplication): Promise<SfApiResponse<string>> {
    try {
      const axiosResponse: AxiosResponse = await axios.put(`${import.meta.env.VITE_APP_LEGACY_API_URL}/application/${applicationType}/${application.uuid}/complete`, application);

      return {
        success: true,
        data: axiosResponse.data.data.id
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async updateDentalAndVisionApplication (applicationType: AppTypeDentalOrVision, application: DentalAndVisionApplication, id: string, token: string): Promise<SfApiResponse> {
    try {
      await axios.put(`${import.meta.env.VITE_APP_LEGACY_API_URL}/application/${applicationType}/${id}`, application, this.getHeaders(token));

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async saveAndExit (applicationType: APPLICATION_TYPES, email: string, application: unknown, uuid: UUID): Promise<SfApiResponse> {
    try {
      await axios.post(`${import.meta.env.VITE_APP_LEGACY_API_URL}/application/${applicationType}/${uuid}/save-and-exit`, { email, application });

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async getApplication (applicationType: AppTypeDentalOrVision, uuid: UUID, applicationFinished?: boolean): Promise<SfApiResponse<DentalAndVisionApplication>> {
    try {
      const applicationRoute = applicationFinished ? 'completed' : 'uncompleted';
      const axiosResponse = await enrollmentAxios.get(`${import.meta.env.VITE_APP_LEGACY_API_URL}/application/${applicationType}/${uuid}/${applicationRoute}`);

      return {
        success: true,
        data: axiosResponse.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async challengeQuestions (challengeQuestion: ChallengeQuestion, type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<ChallengeQuestionResponse>> {
    try {
      const axiosResponse = await axios.post(`${import.meta.env.VITE_APP_LEGACY_API_URL}/${challengeType}/${type}/${uuid}/challenge-question`, challengeQuestion);
      const data = challengeType === ChallengeType.APPLICATION ? axiosResponse.data.data : axiosResponse.data;

      return {
        success: true,
        data: data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async challengeQuestionSettings (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<ChallengeQuestionFields>> {
    try {
      const axiosResponse = await axios.get(`${import.meta.env.VITE_APP_LEGACY_API_URL}/${challengeType}/${type}/${uuid}/challenge-question`);

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async challengeQuestionsOtp (challengeQuestion: ChallengeQuestion, type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<ChallengeQuestionResponse>> {
    try {
      const axiosResponse = await axios.post(`${import.meta.env.VITE_APP_LEGACY_API_URL}/${challengeType}/${type}/${uuid}/challenge`, challengeQuestion);

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async challengeQuestionSettingsOtp (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<ChallengeQuestionSettings>> {
    try {
      const axiosResponse = await axios.get(`${import.meta.env.VITE_APP_LEGACY_API_URL}/${challengeType}/${type}/${uuid}/challenge`);

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async sendSelectedChannel (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID, channel: OtpChannelType): Promise<SfApiResponse<ChallengeQuestionSettings>> {
    try {
      const axiosResponse = await recaptchaAxios.post(`${import.meta.env.VITE_APP_LEGACY_API_URL}/${challengeType}/${type}/${uuid}/otp/send`, {
        channel,
      });

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async sendVerificationCode (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID, verificationCodeRequest: OtpVerificationCodeRequest): Promise<SfApiResponse<string>> {
    try {
      const axiosResponse = await recaptchaAxios.post(`${import.meta.env.VITE_APP_LEGACY_API_URL}/${challengeType}/${type}/${uuid}/otp/verify`, {
        code: verificationCodeRequest.code,
        channel: verificationCodeRequest.channel,
      });

      return {
        success: true,
        data: axiosResponse.data.token
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async getCCAccountData (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<CCAccountDataResponse>> {
    try {
      const axiosResponse = await enrollmentAxios.get(`${import.meta.env.VITE_APP_LEGACY_API_URL}/${challengeType}/${type}/${uuid}/cc-access-code`);

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async sendApplicationFeedback (feedbackInfo: FeedbackInfo, applicationType: APPLICATION_TYPES, uuid: UUID, token?: string): Promise<SfApiResponse> {
    try {
      const { captcha_token, ...rest } = feedbackInfo;
      const axiosResponse = await axios.post(`${import.meta.env.VITE_APP_LEGACY_API_URL}/feedback/${applicationType}/${uuid}`, rest, {
        headers: {
          Token: token,
          'X-Recaptcha-Token': captcha_token ?? ''
        }
      });

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async sendAnonymousFeedback (feedbackInfo: FeedbackInfo): Promise<SfApiResponse> {
    try {
      const { captcha_token, ...rest } = feedbackInfo;
      const axiosResponse = await axios.post(`${import.meta.env.VITE_APP_LEGACY_API_URL}/feedback`, rest, {
        headers: {
          'X-Recaptcha-Token': captcha_token ?? ''
        }
      });

      return {
        success: true,
        data: axiosResponse.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  public async getEffectiveDates (applicationType: APPLICATION_TYPES): Promise <SfApiResponse<Array<EffectiveDatesResponse>>> {
    try {
      const axiosResponse: AxiosResponse = await axios.get(`${import.meta.env.VITE_APP_LEGACY_API_URL}/application/${applicationType}/effective-date`);

      return {
        success: true,
        data: axiosResponse.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawSfError>
      };
    }
  }

  private getHeaders (token: string): {headers: {Token: string}} {
    return { headers: { Token: token } };
  }

}
